import React, {useEffect, useMemo, useRef, useState} from 'react';
import {DirectionalHint, Icon, Text, useTheme} from '@fluentui/react';
import {useGetProductBundleItemsQuery} from '../../store/endpoints/ProductBundleEndpoints';
import {calculateBundleItemFigure} from '../../models/ProductBundles';
import BundlePeek from '../Workspace/BundlePeek';

interface IBundleCardExtrasProps {
    bundleId: string;
    name?: string;
    /** Opens the bundle's full view (the card's own click). */
    onOpen?: () => void;
    /** One tiny line (cost · n items with the peek) for the pinned strip's mini cards. */
    compact?: boolean;
}

const COST = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 2});

/**
 * The cost line and peek eye on a bundle picker card. Items (and their vendor costs) load only once
 * the card scrolls into view, so a long strip of bundles does not fan out into one request per card
 * up front. The range sums each line's figure times its quantity: a line's hand-entered Override Price
 * where it has one, otherwise its cheapest and dearest vendor cost (in-stock vendors preferred).
 */
const BundleCardExtras: React.FC<IBundleCardExtrasProps> = ({bundleId, name, onOpen, compact}) => {
    const theme = useTheme();
    const rootRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const node = rootRef.current;
        if (!node || visible) return;
        if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) setVisible(true);
        }, {rootMargin: '200px'});
        observer.observe(node);
        return () => observer.disconnect();
    }, [visible]);

    const items = useGetProductBundleItemsQuery(bundleId, {skip: !visible});

    const range = useMemo(() => {
        const list = items.data?.items ?? [];
        let low = 0, high = 0, priced = 0, noCost = 0, overridden = 0;
        for (const line of list) {
            const qty = line.details?.quantity ?? 1;
            const figure = calculateBundleItemFigure(line);
            if (!figure) { noCost += 1; continue; }
            low += figure.low * qty;
            high += figure.high * qty;
            priced += 1;
            if (figure.isOverridePrice) overridden += 1;
        }
        return {low, high, priced, noCost, overridden, count: list.length};
    }, [items.data]);

    // No "Cost" prefix: the card is 200px wide and a five-figure range was getting truncated.
    const costText = !visible || items.isFetching
        ? '…'
        : range.priced === 0
            // Not "No cost", which reads as free. Nothing here puts a number on the bundle at all.
            ? (range.count === 0 ? 'No items' : 'Cost unknown')
            : Math.abs(range.high - range.low) < 0.005
                ? COST.format(range.low)
                : `${COST.format(range.low)}–${COST.format(range.high)}`;
    // Anything past the figure (items the range could not put a number on) sits on its own line so the figure never wraps or truncates.
    const detail = range.noCost > 0 && range.priced > 0 ? `${range.noCost} item${range.noCost === 1 ? '' : 's'}, cost unknown` : '';
    // Says which of the two the figure is, because an Override Price beats the vendor cost on the lines
    // that carry one: all cost, all hand-priced, or a mix of the two.
    const rangeTooltip = range.overridden === 0
        ? `Cost of ${range.count} item${range.count === 1 ? '' : 's'}: cheapest to dearest vendor, in-stock vendors preferred`
        : range.overridden === range.priced
            ? `${range.priced} item${range.priced === 1 ? '' : 's'} at the Override Price set on the bundle`
            : `${range.overridden} of ${range.priced} items at the Override Price set on the bundle, the rest at cheapest to dearest vendor cost`;

    if (compact) {
        return (
            <div ref={rootRef} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, fontSize: 10, lineHeight: '14px', color: theme.palette.neutralSecondary}}>
                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={detail || undefined}>{costText}</span>
                <BundlePeek bundleId={bundleId} name={name} onOpen={onOpen} directionalHint={DirectionalHint.bottomCenter}>
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 3, color: theme.palette.themePrimary, whiteSpace: 'nowrap', cursor: 'pointer'}}
                          onClick={(e) => { e.stopPropagation(); onOpen?.(); }}>
                        <Icon iconName='View' styles={{root: {fontSize: 10}}} />{range.count || ''}
                    </span>
                </BundlePeek>
            </div>
        );
    }

    return (
        <div ref={rootRef} style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6, margin: '2px 0 6px'}}>
            {/* Always two lines tall, so the Details button lands at the same height on every card. */}
            <div style={{minWidth: 0, display: 'flex', flexDirection: 'column', height: 36, justifyContent: 'flex-start'}}>
                <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', height: 18, lineHeight: '18px'}}}
                    title={range.count ? rangeTooltip : undefined}>
                    {costText}
                </Text>
                {detail
                    ? <BundlePeek bundleId={bundleId} name={name} filter='noCost' directionalHint={DirectionalHint.topCenter}>
                        <span style={{display: 'block', fontSize: 10, height: 18, lineHeight: '18px', color: theme.palette.neutralTertiary, whiteSpace: 'nowrap', cursor: 'help', textDecoration: 'underline dotted'}}>{detail}</span>
                    </BundlePeek>
                    : <span style={{display: 'block', height: 18}} />}
            </div>
            <BundlePeek bundleId={bundleId} name={name} onOpen={onOpen} directionalHint={DirectionalHint.topCenter}>
                <span
                    role='button'
                    aria-label='Peek at bundle contents'
                    onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
                    style={{display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: theme.palette.themePrimary, cursor: 'pointer', whiteSpace: 'nowrap'}}
                >
                    <Icon iconName='View' styles={{root: {fontSize: 12}}} />
                    {range.count ? `${range.count} item${range.count === 1 ? '' : 's'}` : 'Peek'}
                </span>
            </BundlePeek>
        </div>
    );
};

export default BundleCardExtras;
